import React from 'react';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';

import { Button, Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import type { ThemeHeadingSizes } from '@rover/kibble/styles/themeTypes';
import { HeadingVariant } from '@rover/react-lib/src/components/lists/Carousel/components/Header';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import getBrandDisplayName from '@rover/shared/js/utils/getBrandDisplayName';
import { PetType } from '@rover/types/src/Pet';

import {
  ReviewsCarouselPaginateAction,
  ReviewsCtaClick,
  ViewReviewCard,
} from '../utilities/analyticsEvents';

import ReviewsCarousel, { Review } from './ReviewsCarousel';

// Each locality/non-locality pair is a separate message rather than a conditionally appended
// suffix: translators need the whole sentence to place the locality correctly, and some locales
// can't take a dangling "- {locationName}" tail.
const getHeadingWithLocation = (
  i18n: I18n,
  petType: PetType | undefined,
  brandDisplayName: string,
  locationName: string
): string => {
  switch (petType) {
    case PetType.CAT:
      return i18n._(msg`Cat parents share their ${brandDisplayName} experiences - ${locationName}`);
    case PetType.DOG:
      return i18n._(msg`Dog parents share their ${brandDisplayName} experiences - ${locationName}`);
    case PetType.PUPPY:
      return i18n._(
        msg`Puppy parents share their ${brandDisplayName} experiences - ${locationName}`
      );
    default:
      return i18n._(msg`Pet parents share their ${brandDisplayName} experiences - ${locationName}`);
  }
};

const getHeadingWithoutLocation = (
  i18n: I18n,
  petType: PetType | undefined,
  brandDisplayName: string
): string => {
  switch (petType) {
    case PetType.CAT:
      return i18n._(msg`Cat parents share their ${brandDisplayName} experiences`);
    case PetType.DOG:
      return i18n._(msg`Dog parents share their ${brandDisplayName} experiences`);
    case PetType.PUPPY:
      return i18n._(msg`Puppy parents share their ${brandDisplayName} experiences`);
    default:
      return i18n._(msg`Pet parents share their ${brandDisplayName} experiences`);
  }
};

/** Locality is part of the heading on SEO location pages; the homepage omits it. */
const getHeading = (
  i18n: I18n,
  petType: PetType | undefined,
  brandDisplayName: string,
  locationName?: string
): string =>
  locationName
    ? getHeadingWithLocation(i18n, petType, brandDisplayName, locationName)
    : getHeadingWithoutLocation(i18n, petType, brandDisplayName);

/**
 * The species the carousel is about, or ``undefined`` when it covers more than one.
 *
 * SEO location pages know their species up front and pass it explicitly. The homepage doesn't:
 * its reviews come from the brand's default pet types, which are dog + cat for Rover and Mad
 * Paws but cat-only for Cat in a Flat. Reading it back off the reviews names the species when
 * they all share one and falls back to the generic "Pet parents" heading when they don't.
 */
export const getSharedPetType = (reviews: Review[]): PetType | undefined => {
  const petTypes = new Set(reviews.flatMap((review) => review.pets.map((pet) => pet.petType)));
  if (petTypes.size !== 1) return undefined;

  const [petType] = petTypes;
  return Object.values(PetType).find((value) => value === petType);
};

type Props = {
  /** Omit to derive it from the reviews — see ``getSharedPetType``. */
  petType?: PetType;
  locationName?: string;
  /** Omit when the caller has no slug to report, e.g. the homepage's server-resolved default. */
  internalServiceTypeSlug?: string;
  ctaHref: string;
  cardHrefLabel?: string;
  cardHref?: string;
  getCardHref?: (review: Review) => string | undefined;
  headingVariant?: HeadingVariant;
  headingSize?: ThemeHeadingSizes;
} & Pick<React.ComponentProps<typeof ReviewsCarousel>, 'reviews'> &
  Pick<React.ComponentProps<typeof Section>, 'my' | 'mt' | 'mb' | 'htmlRef'>;

const LandingReviewsCarousel: React.FC<Props> = ({
  reviews,
  petType,
  locationName,
  internalServiceTypeSlug,
  ctaHref,
  cardHref,
  getCardHref,
  cardHrefLabel,
  headingVariant = 'heading',
  headingSize = '400',
  ...sectionProps
}) => {
  const { t, i18n } = useLingui();
  const brandDisplayName = getBrandDisplayName();

  if (!reviews.length) return null;

  return (
    <Section paddingLeft="4x" {...sectionProps}>
      <Flex gap="6x" flexDirection="column" alignItems="center">
        <ReviewsCarousel
          heading={getHeading(
            i18n,
            petType ?? getSharedPetType(reviews),
            brandDisplayName,
            locationName
          )}
          headingVariant={headingVariant}
          headingSize={headingSize}
          reviews={reviews}
          onCardViewed={(index) => emitAnalyticsEvent(new ViewReviewCard({ index }))}
          getCardHref={getCardHref}
          cardProps={{
            href: cardHref,
            hrefLabel: cardHrefLabel ?? t`Find pet care near you`,
          }}
          onPaginate={(paginateParams) =>
            emitAnalyticsEvent(
              new ReviewsCarouselPaginateAction({
                internalServiceTypeSlug,
                ...paginateParams,
              })
            )
          }
        />
        <Button
          width="100%"
          maxWidth={['335px', '375px']}
          href={ctaHref}
          onPress={() => {
            new ReviewsCtaClick({ internalServiceTypeSlug }).fireCrossPageEvent();
          }}
        >
          <Trans>Find pet care near you</Trans>
        </Button>
      </Flex>
    </Section>
  );
};

export default LandingReviewsCarousel;
